import { useCallback, useEffect, useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { PromptWorkspace } from "@/components/prompt/PromptWorkspace";
import { SessionSidebar } from "@/components/prompt/SessionSidebar";

/**
 * /create page. The active session is encoded as `?session=<id>` in the URL
 * so refreshes survive and shareable links work. We read/write the URL
 * directly via window.location + history.replaceState to avoid the type
 * acrobatics that TanStack Router's typed search params would impose on
 * every other navigate-to-/create call site (Header, Dashboard, etc).
 */
function readSessionFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const v = params.get("session");
  return v && v.length > 0 ? v : null;
}

function writeSessionToUrl(sessionId: string | null) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (sessionId) {
    url.searchParams.set("session", sessionId);
  } else {
    url.searchParams.delete("session");
  }
  // pushState so back/forward works as expected.
  window.history.pushState(null, "", url.toString());
}

export function CreatePage() {
  const [activeSessionId, setActiveSessionId] = useState<string | null>(() =>
    readSessionFromUrl(),
  );
  // Bumped on every "new session" so a fresh remount happens even when we're
  // ALREADY on a null (unsaved) session — the workspace re-reads the chosen
  // project kind on mount, so null→null still needs a new key to pick up a
  // kind switch (Motion graphic ↔ SVG graphic).
  const [newSessionNonce, setNewSessionNonce] = useState(0);

  // Keep state in sync with browser back/forward.
  useEffect(() => {
    const onPop = () => setActiveSessionId(readSessionFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const handleSelectSession = useCallback((rootId: string) => {
    writeSessionToUrl(rootId);
    setActiveSessionId(rootId);
  }, []);

  const handleNewSession = useCallback(() => {
    writeSessionToUrl(null);
    setActiveSessionId(null);
    setNewSessionNonce((n) => n + 1);
  }, []);

  const handleSessionCreated = useCallback((rootId: string) => {
    writeSessionToUrl(rootId);
    setActiveSessionId(rootId);
  }, []);

  return (
    <Layout>
      <div className="flex h-full">
        <SessionSidebar
          onSelectSession={handleSelectSession}
          onNewSession={handleNewSession}
          activeSessionId={activeSessionId}
        />
        <div className="flex-1 overflow-y-auto">
          <PromptWorkspace
            // key forces a clean remount when session id changes —
            // PromptWorkspace's per-session state is non-trivial and the
            // sessionId prop change isn't enough to reset everything.
            key={activeSessionId ?? `new-${newSessionNonce}`}
            sessionId={activeSessionId}
            onSessionCreated={handleSessionCreated}
          />
        </div>
      </div>
    </Layout>
  );
}
